import { t } from '@lingui/macro'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { mixpanel } from '@/mixpanel'
import { TaskItem } from '@/modules/task/components/task-item/TaskItem'
import { HomeSectionLayout } from '../HomeSectionLayout'

import type { FC } from 'react'
import type { Task } from '@/modules/task/types/task.types'

type Props = {
  tasks?: Task[]
  numberOfTasks: number
}

export const TaskPreviewSection: FC<Props> = ({ tasks, numberOfTasks }) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const navigate = useNavigate()

  // Methods
  // ------------------------------------------------------------------------- /
  const handleTaskClick = (taskId: string) => {
    mixpanel.track('home_task_card_click', { origin })

    navigate(`/task/${taskId}`)
  }

  // Render
  // ------------------------------------------------------------------------- /

  const renderContent = useCallback(() => {
    if (!tasks?.length) return null

    return (
      <FlexColumn gap={theme.UI.SpacingPx.Space2}>
        {tasks?.map((task) => (
          <TaskItem key={task.id} task={task} onClick={() => handleTaskClick(task.id)} preview={true} />
        ))}
      </FlexColumn>
    )
  }, [tasks])

  // No added value if no tasks
  if (!tasks?.length) return null

  return (
    <HomeSectionLayout
      title={
        numberOfTasks > 1
          ? t({
              id: 'home.task-preview.title-plural',
              message: 'Je hebt {count} taken',
              values: {
                count: numberOfTasks,
              },
            })
          : numberOfTasks === 1
            ? t({
                id: 'home.task-preview.title-singular',
                message: 'Je hebt 1 taak',
              })
            : ''
      }
    >
      <StContainer>
        {renderContent()}

        <Button
          variant='Secondary'
          onClick={() => {
            mixpanel.track('home_show_all_tasks_click', { origin })

            navigate('/task')
          }}
        >
          {numberOfTasks && numberOfTasks > 3
            ? t({
                id: 'home.task-preview.show-all',
                message: 'Toon alle taken',
              })
            : t({ id: 'home.task-preview.button', message: 'Ga naar taken' })}
        </Button>
      </StContainer>
    </HomeSectionLayout>
  )
}

const StContainer = styled(FlexColumn)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  background: ${({ theme }) => theme.Colors.Background.White};
  border: 1px solid ${({ theme }) => theme.Components.Card.Border};
  border-radius: ${({ theme }) => theme.UI.SpacingPx.Space3};
`
